<template>
  <div class="biometric-container">
    <!-- VERSION MARKER: v3.0 - FIXED ADDRESS DATA AND ENDPOINT -->
    <div class="bg-yellow-100 p-2 text-center mb-4 border border-yellow-400 rounded text-xs">
      ИСПРАВЛЕННАЯ ВЕРСИЯ БИОМЕТРИЧЕСКОЙ ВЕРИФИКАЦИИ v3.0 - АДРЕС + ПРАВИЛЬНЫЙ ЭНДПОИНТ
    </div>
    <div v-if="loading" class="loading-wrapper">
      <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      <p class="mt-3 text-gray-600">Подготовка к биометрической верификации...</p>
    </div>
    
    <div v-else-if="error" class="error-wrapper">
      <div class="bg-red-50 p-6 rounded-md text-red-700">
        <h3 class="font-semibold text-lg mb-2">Ошибка</h3>
        <p>{{ errorMessage }}</p>
        <button @click="retry" class="mt-4 px-4 py-2 bg-primary text-white rounded-md hover:bg-blue-600">
          Попробовать снова
        </button>
      </div>
    </div>
    
    <div v-else-if="!sessionStarted" class="start-wrapper">
      <h2 class="text-xl font-semibold mb-4">Биометрическая верификация</h2>
      <p class="mb-6 text-gray-600">
        Для продолжения регистрации вам необходимо пройти биометрическую верификацию личности.
        В процессе вы будете перенаправлены на сервис Biometric.Vision, где вам нужно будет:
      </p>
      <ul class="list-disc pl-5 mb-6 text-gray-600">
        <li>Сделать фотографию своего лица</li>
        <li>Предоставить данные документа удостоверения личности</li>
      </ul>
      <p class="mb-6 text-gray-600">
        После успешного прохождения проверки вы будете автоматически возвращены на наш сайт.
      </p>
      <button 
        @click="startBiometricVerification" 
        class="px-6 py-3 bg-primary text-white rounded-md hover:bg-blue-600"
      >
        Начать верификацию
      </button>
    </div>
    
    <div v-else-if="verificationComplete" class="results-wrapper">
      <h2 class="text-xl font-semibold mb-4">Верификация завершена</h2>
      <div v-if="verificationSuccessful" class="success-wrapper">
        <div class="bg-green-50 p-4 rounded-md mb-6">
          <p class="text-green-700 font-medium">Биометрическая верификация успешно пройдена!</p>
        </div>
        
        <h3 class="font-semibold text-lg mb-3">Результаты верификации (отладочная информация):</h3>
        <div class="bg-gray-50 p-4 rounded-md overflow-auto max-h-[400px]">
          <pre class="text-sm">{{ JSON.stringify(verificationResults, null, 2) }}</pre>
        </div>
        
        <button @click="continueRegistration" class="mt-6 px-6 py-3 bg-primary text-white rounded-md hover:bg-blue-600">
          Продолжить регистрацию
        </button>
      </div>
      <div v-else class="failure-wrapper">
        <div class="bg-red-50 p-4 rounded-md mb-6">
          <p class="text-red-700 font-medium">Не удалось пройти биометрическую верификацию.</p>
        </div>
        <p class="mb-4 text-gray-600">Возможные причины:</p>
        <ul class="list-disc pl-5 mb-6 text-gray-600">
          <li>Низкое качество изображения или освещения</li>
          <li>Данные документа не совпадают с биометрическими данными</li>
          <li>Проверка была прервана или превышено время ожидания</li>
        </ul>
        
        <button @click="retry" class="px-6 py-3 bg-primary text-white rounded-md hover:bg-blue-600">
          Попробовать снова
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BiometricVerification',
  props: {
    userInfo: {
      type: Object,
      default: () => ({
        iin: '',
        phone: '',
        email: ''
      })
    }
  },
  data() {
    return {
      loading: false,
      error: false,
      errorMessage: '',
      sessionId: null,
      redirectUrl: null,
      sessionStarted: false,
      verificationComplete: false,
      verificationSuccessful: false,
      verificationResults: null,
      biometricApiKey: process.env.VUE_APP_BIOMETRIC_API_KEY || 'fWf4MqSd1LDhDj6Enh2zRn6ReHC3c05WzLiwEEDivLEETW4' // Replace with your actual API key
    }
  },
  methods: {
    async startBiometricVerification() {
      this.loading = true;
      try {
        // ИСПРАВЛЕНИЕ: Используем наш API для создания сессии
        const response = await fetch('/api/biometric/request/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            iin: this.userInfo.iin || '',
            phone: this.userInfo.phone || ''
          })
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
        }
        
        const data = await response.json();
        
        if (data.session_id && data.biometric_url) {
          this.sessionId = data.session_id;
          this.redirectUrl = data.biometric_url;
          
          // Store session ID in localStorage to retrieve it after redirect back
          localStorage.setItem('biometricSessionId', this.sessionId);
          
          this.sessionStarted = true;
          this.loading = false;
          
          
          // Redirect to Biometric platform
          window.location.href = this.redirectUrl;
        } else {
          throw new Error('Invalid response from biometric service');
        }
      } catch (error) {
        this.error = true;
        this.errorMessage = error.message || 'Не удалось создать сессию биометрической верификации';
        this.loading = false;
      }
    },
    
    async checkVerificationResult() {
      this.loading = true;
      try {
        // Get the session ID from localStorage
        const sessionId = localStorage.getItem('biometricSessionId');
        
        if (!sessionId) {
          throw new Error('Session ID not found');
        }
        
        // Get verification results
        const response = await fetch(`https://kyc.biometric.kz/api/v1/flows/session/result/?session_id=${sessionId}&flow_api_key=${this.biometricApiKey}`);
        
        if (!response.ok) {
          throw new Error(`API response error: ${response.status} ${response.statusText}`);
        }
        
        const data = await response.json();
        
        this.verificationResults = data;
        
        // Check if verification was successful
        if (data.flow_session_result === true || data.status === "FINISHED") {
          this.verificationSuccessful = true;
          
          // Emit event with verification data
          this.$emit('verification-completed', {
            success: true,
            data: data
          });
        } else {
          this.verificationSuccessful = false;
          
          // Emit event with failure
          this.$emit('verification-completed', {
            success: false,
            data: data
          });
        }
        
        this.verificationComplete = true;
        this.loading = false;
      } catch (error) {
        this.error = true;
        this.errorMessage = error.message || 'Не удалось получить результаты верификации';
        this.loading = false;
      }
    },
    
    continueRegistration() {
      // Если верификация успешна и есть данные для регистрации
      if (this.verificationSuccessful && this.verificationResults) {
        try {
          // Подготавливаем данные для отправки на сервер
          const biometricData = this.verificationResults;
          
          // Извлекаем основные данные для отладки
          let iin = '';
          let firstName = '';
          let lastName = '';
          let middleName = '';
          let dateOfBirth = '';
          let documentImage = null;
          let addressData = null;
          
          // Проверяем наличие основных данных
          if (!biometricData.edocument_result) {
            throw new Error('Отсутствует edocument_result в данных биометрической верификации');
          }
          
          const edoc = biometricData.edocument_result;
          
          // Проверяем наличие result_json
          if (!edoc.result_json || !edoc.result_json.common) {
            throw new Error('Отсутствует result_json.common в данных биометрической верификации');
          }
          
          const common = edoc.result_json.common;
          
          // Проверяем наличие docOwner
          if (!common.docOwner) {
            throw new Error('Отсутствует docOwner в данных биометрической верификации');
          }
          
          const docOwner = common.docOwner;
          
          // СТРОГО получаем ИИН из docOwner.iin
          if (docOwner.iin) {
            iin = docOwner.iin;
          } else {
            throw new Error('ИИН не найден в данных биометрической верификации');
          }
          
          // СТРОГО получаем имя из docOwner.firstName
          if (docOwner.firstName) {
            firstName = docOwner.firstName;
          } else {
            throw new Error('Имя не найдено в данных биометрической верификации');
          }
          
          // СТРОГО получаем фамилию из docOwner.lastName
          if (docOwner.lastName) {
            lastName = docOwner.lastName;
          } else {
            throw new Error('Фамилия не найдена в данных биометрической верификации');
          }
          
          // СТРОГО получаем отчество из docOwner.middleName (если есть)
          if (docOwner.middleName) {
            middleName = docOwner.middleName;
          }
          
          // Получаем дату рождения из extra.date_of_birth (если есть)
          if (edoc.extra && edoc.extra.date_of_birth) {
            dateOfBirth = edoc.extra.date_of_birth;
          }
          
          // Извлекаем фото лица
          if (edoc.face_photo) {
            documentImage = edoc.face_photo;
          }
          
          // НОВОЕ: Извлекаем адресные данные из address_result
          if (biometricData.address_result) {
            addressData = biometricData.address_result;
            
            // Проверяем структуру адреса (новый объектный формат)
            if (addressData.address && typeof addressData.address === 'object') {
              const addr = addressData.address;
            } else if (typeof addressData.address === 'string') {
            }
          } else {
      // No action needed
          }
          
          // Для отладки - полное сравнение извлеченных данных с исходными
          
          // Получаем session_id из localStorage
          const sessionId = localStorage.getItem('biometricSessionId');
          if (!sessionId) {
            throw new Error('Session ID not found');
          }
          
          
          // ИСПРАВЛЕНИЕ: Отправляем полные данные верификации на новый эндпоинт
          fetch('/api/biometric/save-result/', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify({
              session_id: sessionId,
              verification_data: biometricData
            })
          })
          .then(response => {
            if (!response.ok) {
              return response.json().then(errorData => {
                // Пытаемся получить детальную информацию об ошибке
                const errorMessage = JSON.stringify(errorData);
                throw new Error(`Registration failed: ${response.status}. ${errorMessage}`);
              });
            }
            return response.json();
          })
          .then(data => {
            
            // Сохраняем токен в localStorage для последующей аутентификации
            if (data.access_token) {
              localStorage.setItem('authToken', data.access_token);
            }
            
            // Сохраняем данные пользователя
            if (data.user) {
              localStorage.setItem('userData', JSON.stringify(data.user));
            }
            
            // Передаем результат регистрации в родительский компонент
            this.$emit('registration-completed', {
              success: true,
              data: data
            });
          })
          .catch(error => {
            this.$emit('registration-completed', {
              success: false,
              error: error.message
            });
          });
        } catch (error) {
          this.$emit('registration-completed', {
            success: false,
            error: error.message || 'Error preparing registration data'
          });
        }
      } else {
        // Если верификация не была успешной, просто передаем результаты
        this.$emit('continue-registration', this.verificationResults);
      }
    },
    
    retry() {
      // Reset state to try again
      this.loading = false;
      this.error = false;
      this.errorMessage = '';
      this.sessionId = null;
      this.redirectUrl = null;
      this.sessionStarted = false;
      this.verificationComplete = false;
      this.verificationSuccessful = false;
      this.verificationResults = null;
      
      // Clear localStorage
      localStorage.removeItem('biometricSessionId');
    },
    
    // Новый метод для обработки возврата с Biometric Flow
    handleBiometricReturn() {
      // Добавляем небольшую задержку для стабильности
      setTimeout(() => {
        this.checkVerificationResult().then(() => {
          // Если верификация успешна, автоматически продолжаем регистрацию
          if (this.verificationSuccessful) {
            setTimeout(() => this.continueRegistration(), 1000);
          }
        }).catch(error => {
          this.error = true;
          this.errorMessage = error.message || 'Ошибка при проверке результатов верификации';
        });
      }, 500);
    }
  },
  mounted() {
    // Check if we're returning from Biometric verification
    const sessionId = localStorage.getItem('biometricSessionId');
    
    const urlParams = new URLSearchParams(window.location.search);
    const hasReturnParams = urlParams.has('status') || urlParams.has('flow_result');
    
    // Проверяем, есть ли в URL параметры возврата от Biometric Flow
    if (hasReturnParams && sessionId) {
      this.sessionStarted = true;
      this.handleBiometricReturn();
      return;
    }
    
    // Fix: Changed from includes('biometric-register') to includes('/biometric-register')
    // This ensures we're checking for the full path segment
    if (sessionId && window.location.pathname.includes('/biometric-register')) {
      this.sessionStarted = true;
      this.handleBiometricReturn();
    } else {
      // No action needed
    }
  },
  
  // Добавляем хук для проверки URL параметров при изменении маршрута
  watch: {
    '$route': {
      handler(newRoute) {
        const sessionId = localStorage.getItem('biometricSessionId');
        const hasReturnParams = newRoute.query.status || newRoute.query.flow_result;
        
        if (sessionId && hasReturnParams) {
          this.sessionStarted = true;
          this.handleBiometricReturn();
        }
      },
      immediate: true
    }
  }
}
</script>

<style scoped>
.biometric-container {
  max-width: 600px;
  margin: 0 auto;
  padding: 20px;
}

.loading-wrapper, .error-wrapper, .start-wrapper, .results-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.loading-wrapper {
  padding: 40px 0;
}

.error-wrapper, .failure-wrapper {
  width: 100%;
}

pre {
  text-align: left;
  font-family: monospace;
  white-space: pre-wrap;
  word-break: break-all;
}
</style> 